/* =========================================================================
   assets/theme-core.js
   Comportamentos globais: reveal ao scroll, header sólido, alternância de tema.
   Web Components nativos — nenhum framework.
   ========================================================================= */

/* --- <reveal-on-scroll> -------------------------------------------------
   Substitui o componente Reveal do React. Aplica .is-visible aos filhos
   marcados com [data-reveal] (ou ao próprio elemento) quando entram na tela.
   Atributos: data-delay (ms), data-threshold (0-1), data-once
------------------------------------------------------------------------- */
(function () {
  function init() {
    const targets = document.querySelectorAll('[data-reveal], .reveal, .worn-item');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = parseInt(entry.target.dataset.delay || '0', 10);
          setTimeout(() => entry.target.classList.add('is-visible'), delay);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    targets.forEach((el) => observer.observe(el));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --- <sticky-header> ----------------------------------------------------
   Header transparente no topo da home, sólido depois de 12px de scroll
   ou em qualquer outro template. data-always-solid="true" para páginas
   internas (definido em Liquid, então o HTML já nasce correto no servidor).
------------------------------------------------------------------------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    /* Transparente só quando a section pede overlay (hero da home). */
    this.overlay = this.classList.contains('fea-header--overlay');
    if (!this.overlay) {
      this.setAttribute('data-solid', 'true');
      return;
    }
    this.onScroll = this.onScroll.bind(this);
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  onScroll() {
    this.setAttribute('data-solid', window.scrollY > 12 ? 'true' : 'false');
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('sticky-header', StickyHeader);

/* --- <theme-toggle> -----------------------------------------------------
   SOLAR <-> ATMOSFERA. Persiste em localStorage (fea.scheme).
   O valor inicial já é aplicado no <head> para evitar flash.
------------------------------------------------------------------------- */
class ThemeToggle extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll('[data-scheme]');
    this.sync();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-scheme]');
      if (!button) return;
      const scheme = button.dataset.scheme;
      document.documentElement.setAttribute('data-color-scheme', scheme);
      try {
        localStorage.setItem('fea.scheme', scheme);
      } catch (e) {}
      this.sync();
    });
  }

  sync() {
    const current = document.documentElement.getAttribute('data-color-scheme');
    this.buttons.forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.scheme === current));
    });
  }
}
customElements.define('theme-toggle', ThemeToggle);

/* --- Utilitário global de formatação de dinheiro ------------------------ */
window.FEA = window.FEA || {};
window.FEA.formatMoney = function (cents) {
  const format = window.FEA.moneyFormat || '{{amount}}';
  const value = (cents / 100).toFixed(2);
  return format.replace(/\{\{\s*amount\s*\}\}/, value);
};

/* --- Setas dos carrosséis ------------------------------------------------
   [data-carousel-step] rola o [data-carousel-track] da mesma section
   por uma "página" (largura visível). Desabilita a seta no início/fim.
------------------------------------------------------------------------- */
(function () {
  function trackFor(button) {
    const section = button.closest('section');
    return section ? section.querySelector('[data-carousel-track]') : null;
  }

  function sync(track) {
    const section = track.closest('section');
    if (!section) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    section.querySelectorAll('[data-carousel-step]').forEach((btn) => {
      const dir = Number(btn.dataset.carouselStep);
      btn.disabled = dir < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max;
    });
    const index = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
    const current = section.querySelector('[data-carousel-current]');
    if (current) current.textContent = String(index + 1);
    section.querySelectorAll('[data-carousel-dot]').forEach((dot) => {
      const active = Number(dot.dataset.carouselDot) === index;
      dot.setAttribute('aria-current', String(active));
      /* Mantém a miniatura ativa visível na faixa, sem rolar a página. */
      const strip = active && dot.closest('[data-carousel-thumbs]');
      if (strip) {
        const left = dot.offsetLeft - strip.offsetLeft - (strip.clientWidth - dot.offsetWidth) / 2;
        const top = dot.offsetTop - strip.offsetTop - (strip.clientHeight - dot.offsetHeight) / 2;
        strip.scrollTo({ left: Math.max(0, left), top: Math.max(0, top), behavior: 'smooth' });
      }
    });
  }

  document.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-carousel-dot]');
    if (!dot) return;
    const track = trackFor(dot);
    if (!track) return;
    track.scrollTo({ left: Number(dot.dataset.carouselDot) * track.clientWidth, behavior: 'smooth' });
  });

  /* Troca automática ([data-autoplay] em segundos na section). Volta ao
     início no fim; pausa com o ponteiro em cima, ao tocar, com a aba
     oculta e para quem pede movimento reduzido. */
  function autoplay(track) {
    const section = track.closest('[data-autoplay]');
    const seconds = section ? Number(section.dataset.autoplay) : 0;
    if (!seconds || track.children.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let paused = false;
    let resumeTimer;
    section.addEventListener('pointerenter', () => { paused = true; });
    section.addEventListener('pointerleave', () => { paused = false; });
    track.addEventListener('touchstart', () => {
      paused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { paused = false; }, seconds * 2000);
    }, { passive: true });
    setInterval(() => {
      if (paused || document.hidden) return;
      const page = track.clientWidth;
      const next = Math.round(track.scrollLeft / page) + 1;
      track.scrollTo({ left: next >= track.children.length ? 0 : next * page, behavior: 'smooth' });
    }, seconds * 1000);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-carousel-step]');
    if (!button) return;
    const track = trackFor(button);
    if (!track) return;
    /* scrollTo para uma posição exata: scrollBy suave + scroll-snap
       às vezes é cancelado pelo Chrome no primeiro gesto. */
    const page = track.clientWidth;
    const index = Math.round(track.scrollLeft / page) + Number(button.dataset.carouselStep);
    const max = track.scrollWidth - page;
    track.scrollTo({ left: Math.max(0, Math.min(index * page, max)), behavior: 'smooth' });
  });

  function init() {
    document.querySelectorAll('[data-carousel-track]').forEach((track) => {
      sync(track);
      track.addEventListener('scroll', () => sync(track), { passive: true });
      autoplay(track);
    });
    window.addEventListener('resize', () => {
      document.querySelectorAll('[data-carousel-track]').forEach(sync);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --- Filtro do catálogo --------------------------------------------------
   [data-catalog-filter] troca as peças visíveis na hora, sem recarregar.
   Sem JS, cada filtro é um link comum para a página da coleção.
------------------------------------------------------------------------- */
(function () {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-catalog-filter]');
    if (!link) return;
    const catalog = link.closest('[data-catalog]');
    if (!catalog) return;
    event.preventDefault();
    const handle = link.dataset.catalogFilter;
    catalog.querySelectorAll('[data-catalog-filter]').forEach((el) => {
      el.setAttribute('aria-pressed', String(el === link));
    });
    catalog.querySelectorAll('[data-collections]').forEach((item) => {
      item.hidden = handle !== 'all' && !item.dataset.collections.includes(' ' + handle + ' ');
    });
    const grid = catalog.querySelector('[data-catalog-grid]');
    if (grid && grid.getBoundingClientRect().top < 0) {
      catalog.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
})();

/* --- Vídeos sob demanda ------------------------------------------------
   [data-lazy-video][data-src]: só baixa quando o vídeo chega perto da
   tela; toca sem som quando visível e pausa quando sai.
------------------------------------------------------------------------- */
(function () {
  function init() {
    const videos = document.querySelectorAll('video[data-lazy-video]');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (!video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.preload = 'auto';
          }
          if (!reduce) video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '300px 0px' });
    videos.forEach((v) => observer.observe(v));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --- Mais vendidos -------------------------------------------------------
   Troca a grade pela ordem real de vendas da Shopify, sem recarregar.
   Se a busca falhar, a grade do servidor continua na tela.
------------------------------------------------------------------------- */
(function () {
  const section = document.querySelector('[data-bestsellers]');
  if (!section) return;
  const limit = Number(section.dataset.bestsellers) || 8;
  const grid = section.querySelector('[data-bestsellers-grid]');
  fetch('/collections/all?sort_by=best-selling&section_id=best-sellers')
    .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-bestsellers-grid]');
      if (!fresh || !fresh.children.length) return;
      [...fresh.children].slice(limit).forEach((el) => el.remove());
      fresh.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      grid.innerHTML = fresh.innerHTML;
    })
    .catch(() => {});
})();

/* --- Pré-carrega as fotos dos carrosséis -----------------------------------
   Fotos fora da tela num carrossel lateral só baixariam ao deslizar, e a
   cliente veria o quadro vazio. Depois que a página termina de carregar,
   elas passam a baixar em segundo plano, na ordem.
------------------------------------------------------------------------- */
(function () {
  function warm() {
    document.querySelectorAll('[data-carousel-track] img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }
  const run = () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 2500 }) : setTimeout(warm, 1200));
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run);
})();

/* --- Som do filme da hero ---------------------------------------------
   O filme começa mudo. O botão liga o áudio original do vídeo com uma
   entrada suave; se a hero sair da tela, o som desliga sozinho para não
   voltar alto de surpresa quando a pessoa rolar de volta.
------------------------------------------------------------------------ */
(function () {
  function init() {
    document.querySelectorAll('[data-hero-sound]').forEach((btn) => {
      const hero = btn.closest('.hero');
      const video = hero && hero.querySelector('video');
      if (!video) return;
      const label = btn.querySelector('.hero__sound-label');
      let fade = null;

      function render(on) {
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-on', on);
        const texto = on ? btn.dataset.labelOff : btn.dataset.labelOn;
        if (label) label.textContent = texto;
        btn.title = texto;
      }

      function silence() {
        clearInterval(fade);
        video.muted = true;
        render(false);
      }

      btn.addEventListener('click', () => {
        if (!video.muted) { silence(); return; }
        if (!video.src && video.dataset.src) {
          video.src = video.dataset.src;
          video.preload = 'auto';
        }
        clearInterval(fade);
        try { video.volume = 0; } catch (e) {}
        video.muted = false;
        video.play().catch(() => {});
        // iOS ignora volume programático (fica em 1); nos demais, entra suave.
        fade = setInterval(() => {
          try { video.volume = Math.min(1, video.volume + 0.08); } catch (e) {}
          if (video.volume >= 1) clearInterval(fade);
        }, 60);
        render(true);
      });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (!entry.isIntersecting && !video.muted) silence(); });
        }, { threshold: 0.15 }).observe(hero);
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden && !video.muted) silence(); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
